import { dialog } from "../../../../../nq-component";
import BaseFormPresenter from "../../../../../base/BaseFormPresenter";
import DialogModal from "../../../../../components/Dialog";
import {
  _sumPartialCosts,
  addNum,
  calculateScale,
  computePartialCosts,
  computePartialCostsForOtherComponents,
  computeTotalCost,
  toNum,
} from "../../../../../componentCostUtils";

class ProductFormPresenter extends BaseFormPresenter {
  _handleSaveSuccess() {
    this.change = {};

    dialog.fire({
      html: (
        <DialogModal
          icon="bi bi-check-circle-fill"
          iconColor="#82c14a"
          title="Saved Successfully"
          message="Your changes have been saved successfully."
          isCancelButton={false}
          onSubmit={() => {
            this.view.navigateBack();
            dialog.close();
          }}
          onClose={() => dialog.close()}
        />
      ),
      footer: false,
    });
  }

  _handleSaveError(error) {
    console.error("Save failed:", error);

    dialog.fire({
      html: (
        <DialogModal
          icon="bi bi-exclamation-triangle-fill"
          iconColor="#dc3545"
          title="Save Failed"
          message={`Failed to save: ${error.message || "Unknown error"}`}
          isCancelButton={false}
          onSubmit={() => dialog.close()}
          onClose={() => dialog.close()}
        />
      ),
      footer: false,
    });

    throw error;
  }

  onChange(value, field) {
    this.change[field] = value;
    const current = this.view.state.object || {};
    const next = { ...current, ...this.object, [field]: value };

    if (
      Array.isArray(current.raw_materials) &&
      !Array.isArray(next.raw_materials)
    ) {
      next.raw_materials = current.raw_materials;
    }
    // keep presenter copy in sync
    this.object = next;
    // update the view
    this.view.setObject(next);
  }

  async getObject() {
    const collection = this.view.getCollectionName();
    const id = this.object.id || this.view.getObjectId();
    if (id) {
      const params = { include: ["raw_materials", "raw_materials.uom", "uom"] };
      try {
        this.view.showProgress();
        this.object = await this.getObjectUseCase.execute(collection, id, {
          params,
        });
        this.view.hideProgress();
        this.view.setObject(this.object);
      } catch (error) {
        this.view.hideProgress();
        this.view.showError(error);
      }
    }
  }

  _processComponents(components) {
    return computePartialCostsForOtherComponents(components).map((comp) => {
      const copy = { ...comp };

      if (copy.quantity !== undefined) {
        copy.quantity = String(copy.quantity);
      }

      return copy;
    });
  }

  _processRawMaterials(materials) {
    return computePartialCosts(materials).map((item) => ({ ...item }));
  }

  _processProducts(products) {
    return products.map((product) => {
      const computedComponents = computePartialCostsForOtherComponents(
        product?.components || [],
      );

      const computedOthers = computePartialCosts(
        product?.others_raw_materials || [],
      );

      const componentsCost = _sumPartialCosts(computedComponents);
      const othersCost = _sumPartialCosts(computedOthers);
      const productPartialCost = componentsCost + othersCost;

      const totalCost = productPartialCost * (product?.quantity || 0);

      return {
        ...product,
        components: computedComponents,
        others_raw_materials: computedOthers,
        partial_cost: totalCost.toFixed(2),
      };
    });
  }

  _processAllMaterials(object) {
    const products = this._processProducts(object?.products || []);
    const rawMaterials = this._processRawMaterials(object?.raw_materials || []);
    const othersRawMaterials = this._processRawMaterials(
      object?.others_raw_materials || [],
    );
    const components = this._processComponents(object?.components || []);

    const totalCost = computeTotalCost(
      products,
      components,
      othersRawMaterials,
    );

    return {
      raw_materials: rawMaterials,
      others_raw_materials: othersRawMaterials,
      components,
      products,
      cost: totalCost,
    };
  }

  async save() {
    const collection = this.view.getCollectionName();
    const object = this.view.state.object;

    if (this.object.id) {
      this.change.id = this.object.id;
    } else {
      this.change.acl = this.view.getAcl();
    }

    try {
      const processedData = this._processAllMaterials(object);
      Object.assign(this.change, processedData);
      await this.upsertUseCase.execute(collection, this.change);

      this._handleSaveSuccess();
    } catch (error) {
      this._handleSaveError(error);
    }
  }

  async submit() {
    try {
      this.view.submitting();
      dialog.close();
      await this.save();
    } catch (error) {
      this.view.submissionError(error);
      this.view.showError(error);
    }
  }

  _mergeSelectedProducts(selectedItems) {
    this.view.setState((prev) => {
      const prevObject = prev.object || {};

      return {
        object: {
          ...prevObject,
          products: this._mergeByIdSimple(
            prevObject.products || [],
            selectedItems,
          ),
          components: this._mergeWithQuantity(
            prevObject.components || [],
            selectedItems.flatMap((p) => p.components || []),
          ),
          raw_materials: this._mergeWithQuantity(
            prevObject.raw_materials || [],
            selectedItems.flatMap((p) => p.raw_materials || []),
          ),
        },
      };
    });
  }

  _mergeByIdSimple(existing, newItems) {
    const map = new Map(existing.map((item) => [item.id, item]));
    newItems.forEach((item) => map.set(item.id, item));
    return Array.from(map.values());
  }

  _mergeWithQuantity(existing, newItems) {
    const map = new Map(
      existing.map((item) => [
        item.id,
        { ...item, quantity: parseFloat(item.quantity) || 0 },
      ]),
    );

    newItems.forEach((item) => {
      const existingItem = map.get(item.id);
      const newQty = parseFloat(item.quantity) || 0;

      map.set(item.id, {
        ...(existingItem || item),
        quantity: (existingItem?.quantity || 0) + newQty,
      });
    });

    return Array.from(map.values()).map((item) => ({
      ...item,
      quantity: item.quantity.toString(),
    }));
  }

  _updateProductQuantityAndRecalculate(index, newQuantity) {
    this.view.setState((prev) => {
      const prevObject = prev.object || {};

      const products = [...(prevObject.products || [])];
      products[index] = { ...products[index], quantity: newQuantity };

      const components = this._calculateScaledItems(products, "components");
      const rawMaterials = this._calculateScaledItems(
        products,
        "raw_materials",
      );

      return {
        object: {
          ...prevObject,
          products,
          components,
          raw_materials: rawMaterials,
        },
      };
    });
  }

  _calculateScaledItems(products, itemKey) {
    const itemMap = new Map();

    products.forEach((product) => {
      const multiplier = parseFloat(product.quantity) || 0;

      (product[itemKey] || []).forEach((item) => {
        const baseQty = parseFloat(item.quantity) || 0;
        const scaledQty = baseQty * multiplier;
        const existing = itemMap.get(item.id);

        itemMap.set(item.id, {
          ...item,
          quantity: (existing?.quantity || 0) + scaledQty,
        });
      });
    });

    return Array.from(itemMap.values()).map((item) => ({
      ...item,
      quantity: item.quantity.toString(),
    }));
  }

  _mergeSelectedComponents(selectedItems) {
    this.view.setState((prev) => {
      const prevObject = prev.object || {};
      const components = this.mergeComponents(
        prevObject.components || [],
        selectedItems,
      );
      const raw_materials = this.mergeRawMaterials(
        prevObject.raw_materials || [],
        selectedItems,
      );

      return {
        object: { ...prevObject, components, raw_materials },
      };
    });
  }

  mergeComponents(existing, incoming) {
    const componentMap = new Map(existing.map((c) => [c.id, c]));

    incoming.forEach((comp) => {
      const existingComp = componentMap.get(comp.id);
      componentMap.set(comp.id, {
        ...comp,
        stock_quantity:
          existingComp?.stock_quantity ?? comp.stock_quantity ?? "",
      });
    });

    return Array.from(componentMap.values());
  }

  mergeRawMaterials(existing, selectedComponents) {
    const rawMap = new Map(existing.map((rm) => [rm.id, rm]));

    selectedComponents.forEach((comp) => {
      const parentScale = calculateScale(comp);

      this.accumulateRawMaterials(
        comp.raw_materials || [],
        parentScale,
        rawMap,
      );

      (comp.otherComponents || []).forEach((child) => {
        const childUsedQty = toNum(child.quantity) * parentScale;
        const childScale = calculateScale(child, childUsedQty);
        this.accumulateRawMaterials(
          child.raw_materials || [],
          childScale,
          rawMap,
        );
      });
    });
    return Array.from(rawMap.values());
  }

  accumulateRawMaterials(rawList, scale, rawMap) {
    rawList.forEach((raw) => {
      const existing = rawMap.get(raw.id);
      const scaledQty = toNum(raw.quantity) * scale;
      const unitCost = this.deriveUnitCost(raw);
      const scaledCost = unitCost * scaledQty;

      rawMap.set(raw.id, {
        ...(existing || raw),
        quantity: addNum(existing?.quantity, scaledQty),
        partial_cost: addNum(existing?.partial_cost, scaledCost, 2),
        stock_quantity: existing?.stock_quantity ?? raw.stock_quantity ?? "",
      });
    });
  }

  deriveUnitCost(rm) {
    const q = toNum(rm.quantity);
    const pc = toNum(rm.partial_cost);
    if (pc && q) return pc / q;

    const cost = toNum(rm.cost);
    const stock = toNum(rm.stock_quantity);
    return cost && stock ? cost / stock : 0;
  }

  _mergeSelectedRawMaterials(selectedItems) {
    this.view.setState((prev) => ({
      object: {
        ...prev.object,
        raw_materials: this.mergeItems(
          prev.object.raw_materials || [],
          selectedItems,
        ),
      },
    }));
  }

  mergeItems(existing, incoming) {
    const itemMap = new Map(existing.map((item) => [item.id, item]));

    incoming.forEach((item) => {
      const existingItem = itemMap.get(item.id);
      itemMap.set(item.id, {
        ...item,
        stock_quantity:
          existingItem?.stock_quantity ?? item.stock_quantity ?? "",
      });
    });

    return Array.from(itemMap.values());
  }

  _mergeSelectedOthersRawMaterials(selectedItems) {
    this.view.setState((prev) => ({
      object: {
        ...prev.object,
        others_raw_materials: this.mergeItemsByStockQuantity(
          prev.object.others_raw_materials || [],
          selectedItems,
        ),
      },
    }));
  }

  mergeItemsByStockQuantity(existing, incoming) {
    const itemMap = new Map(existing.map((item) => [item.id, item]));

    incoming.forEach((item) => {
      const existingItem = itemMap.get(item.id);
      itemMap.set(item.id, {
        ...item,
        stock_quantity:
          existingItem?.stock_quantity ?? item.stock_quantity ?? "",
      });
    });

    return Array.from(itemMap.values());
  }
}

export default ProductFormPresenter;
